import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  isErrorUserPendingApproval,
  isForbiddenError,
  storableError,
  isNetworkError,
} from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';
import * as listingVisibility from '../../util/listingVisibility';
import { stripTutulistEncoreQueryParam } from '../../util/tutulist';
import { debugInfiniteScroll } from '../../util/infiniteScrollDebug';
import * as log from '../../util/log';
import { withPrivateListingsExcluded } from '../../util/publicListingQuery';
import {
  getIgnoredSearchQueryParamKeys,
  stripIgnoredSearchQueryParams,
} from '../../util/searchQueryParams';
import { getSearchPageDefaultFiltersConfig } from './SearchPage.shared';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { resolveListingSort } from '../../util/listingSort';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;
const MAX_CONSECUTIVE_HOLES = 5;
const shouldLogSearchDebug = process.env.NODE_ENV === 'development';
const shouldLogPrivateListingsDebug =
  typeof process !== 'undefined' && process.env.DEBUG_PRIVATE_LISTINGS === 'true';
const { filterPublicListings, isPrivateListing } = listingVisibility;

const logSearchDebug = (label, payload) => {
  if (!shouldLogSearchDebug || typeof window === 'undefined') {
    return;
  }

  // eslint-disable-next-line no-console
  console.debug(`[search-debug] ${label}`, payload);
};

// ================ Helper Functions ================ //

const resultIds = data => {
  const listings = Array.isArray(data?.data) ? data.data : [];
  return filterPublicListings(listings).map(l => l.id);
};

const mergeResultIds = (existingIds, incomingIds) => {
  const seenIds = new Set(existingIds.map(id => id.uuid));
  const merged = [...existingIds];

  incomingIds.forEach(id => {
    if (!seenIds.has(id.uuid)) {
      seenIds.add(id.uuid);
      merged.push(id);
    }
  });

  return merged;
};

const getHighestLoadedPage = (loadedPages = [], fallbackPage = 0) => {
  if (Array.isArray(loadedPages) && loadedPages.length > 0) {
    return loadedPages.reduce((maxLoadedPage, pageNumber) => {
      const normalizedPage = Number(pageNumber || 0);
      return normalizedPage > maxLoadedPage ? normalizedPage : maxLoadedPage;
    }, 0);
  }

  return Math.max(0, Number(fallbackPage || 0));
};

const normalizeQueryKeyValue = value => {
  if (Array.isArray(value)) {
    return value.map(v => normalizeQueryKeyValue(v));
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((picked, key) => {
        return { ...picked, [key]: normalizeQueryKeyValue(value[key]) };
      }, {});
  }
  return value;
};

export const createSearchQueryKey = searchParams => {
  const excludedKeys = new Set([
    'page',
    'perPage',
    'include',
    'fields.listing',
    'fields.user',
    'fields.image',
    'limit.images',
  ]);

  const normalizedParams = Object.entries(searchParams || {})
    .filter(([key, value]) => {
      if (excludedKeys.has(key)) {
        return false;
      }
      return value !== null && typeof value !== 'undefined' && value !== '';
    })
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce((picked, [key, value]) => {
      return { ...picked, [key]: normalizeQueryKeyValue(value) };
    }, {});

  return JSON.stringify(normalizedParams);
};

const buildSearchQueryKeyParams = ({ queryParams, config, listingTypePathParam }) => {
  const paramsWithoutDates = { ...queryParams };
  delete paramsWithoutDates.dates;
  delete paramsWithoutDates.startDate;
  delete paramsWithoutDates.endDate;
  const { page, perPage, address, origin, ...rest } = paramsWithoutDates;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
  const listingTypeMaybe = listingTypePathParam ? { listingTypePathParam } : {};

  return {
    ...rest,
    ...originMaybe,
    ...listingTypeMaybe,
  };
};

export const buildCanonicalSearchQueryKey = ({ queryParams, config, listingTypePathParam }) =>
  createSearchQueryKey(
    buildSearchQueryKeyParams({
      queryParams,
      config,
      listingTypePathParam,
    })
  );

const summarizeSearchLogContext = ({ searchParams = {}, requestedPage, append, queryKey }) => {
  const activeFilterKeys = Object.keys(searchParams || {})
    .filter(key => {
      const value = searchParams?.[key];
      return value !== null && typeof value !== 'undefined' && value !== '';
    })
    .slice(0, 20);

  return {
    activeFilterKeys,
    append: Boolean(append),
    category: searchParams?.pub_categoryLevel1 || searchParams?.pub_category || null,
    keywords:
      typeof searchParams?.keywords === 'string' && searchParams.keywords.trim().length > 0
        ? searchParams.keywords.trim().slice(0, 120)
        : null,
    listingType: searchParams?.pub_listingType || null,
    mapMode: Boolean(searchParams?.mapSearch),
    page: Number(requestedPage || searchParams?.page || 1) || 1,
    price: searchParams?.price || null,
    queryKey: queryKey || createSearchQueryKey(searchParams || {}),
    queryParamCount: activeFilterKeys.length,
    resultCount: null,
    routeName: 'SearchPage',
    sort: searchParams?.sort || null,
  };
};

const resolveRequestUrl = config => {
  const { baseURL, url } = config || {};

  if (!baseURL && !url) {
    return null;
  }

  try {
    return baseURL ? new URL(url || '', baseURL).toString() : url;
  } catch (error) {
    return [baseURL, url].filter(Boolean).join('');
  }
};

const getBrowserNetworkContext = () => {
  if (typeof navigator === 'undefined') {
    return {
      online: null,
      connection: null,
      visibilityState: null,
    };
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  return {
    online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
    connection: connection
      ? {
          effectiveType: connection.effectiveType || null,
          type: connection.type || null,
          downlink: typeof connection.downlink === 'number' ? connection.downlink : null,
          rtt: typeof connection.rtt === 'number' ? connection.rtt : null,
          saveData: typeof connection.saveData === 'boolean' ? connection.saveData : null,
        }
      : null,
    visibilityState:
      typeof document !== 'undefined' && document.visibilityState ? document.visibilityState : null,
  };
};

const summarizeSearchRequestDiagnostics = error => {
  const config = error?.config || {};
  const request = error?.request;
  const response = error?.response;
  const status =
    error?.status ??
    response?.status ??
    (typeof request?.status === 'number' ? request.status : null);
  const hasResponse = Boolean(response || error?.data);
  const isNetworkError =
    error?.code === 'ERR_NETWORK' ||
    (error?.name === 'AxiosError' && error?.message === 'Network Error') ||
    (Boolean(request) && !hasResponse && (status === null || status === 0));

  return {
    kind: isNetworkError ? 'network' : 'response',
    errorName: error?.name || null,
    errorMessage: error?.message || null,
    errorCode: error?.code || null,
    status,
    statusText: error?.statusText || response?.statusText || null,
    request: {
      method: config?.method ? `${config.method}`.toUpperCase() : null,
      url: resolveRequestUrl(config),
      timeoutMs: typeof config?.timeout === 'number' ? config.timeout : null,
      withCredentials: typeof config?.withCredentials === 'boolean' ? config.withCredentials : null,
      responseType: config?.responseType || null,
    },
    transport: {
      hasRequest: Boolean(request),
      hasResponse,
      readyState: typeof request?.readyState === 'number' ? request.readyState : null,
      responseURL: request?.responseURL || response?.request?.responseURL || null,
    },
    browser: getBrowserNetworkContext(),
  };
};

const normalizeLegacyCategoryParamForSearch = (queryParams = {}, config) => {
  const categoryConfig = config?.search?.defaultFilters?.find(f => f.schemaType === 'category');
  const categories = config?.categoryConfiguration?.categories || [];
  if (!categoryConfig || !Array.isArray(categories)) {
    return queryParams;
  }

  const { key, scope } = categoryConfig;
  const categoryParamPrefix = constructQueryParamName(key, scope);
  const categoryLevelOneParam = `${categoryParamPrefix}1`;
  const legacyCategoryParam = constructQueryParamName('category', scope);
  const hasNestedCategoryParams = Object.keys(queryParams).some(paramName =>
    paramName.startsWith(categoryParamPrefix)
  );

  const legacyCategoryValueRaw = queryParams?.[legacyCategoryParam];
  const legacyCategoryValue =
    typeof legacyCategoryValueRaw !== 'undefined' ? `${legacyCategoryValueRaw}` : undefined;
  const paramsWithoutLegacy = { ...queryParams };
  delete paramsWithoutLegacy[legacyCategoryParam];

  if (hasNestedCategoryParams || !legacyCategoryValue) {
    return paramsWithoutLegacy;
  }

  const hasValidLegacyCategory = categories.some(category => category.id === legacyCategoryValue);
  return hasValidLegacyCategory
    ? { ...paramsWithoutLegacy, [categoryLevelOneParam]: legacyCategoryValue }
    : paramsWithoutLegacy;
};

const stripMobileSearchUIParams = (queryParams = {}) => {
  const { mobilemenu: _mobileMenu, mobilesearch: _mobileSearch, ...rest } = queryParams;
  return rest;
};

const getValidatedQueryParamsFromSearch = ({ search, config, currentPathParams = {} }) => {
  const parsedQueryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const ignoredQueryParamKeys = getIgnoredSearchQueryParamKeys(parsedQueryParams);
  const normalizedQueryParams = normalizeLegacyCategoryParamForSearch(
    stripTutulistEncoreQueryParam(stripIgnoredSearchQueryParams(parsedQueryParams)),
    config
  );

  return {
    ignoredQueryParamKeys,
    normalizedQueryParams,
    queryParams: stripMobileSearchUIParams(normalizedQueryParams),
  };
};

export const createListingSearchParams = ({
  queryParams,
  config,
  listingTypePathParam,
  pageOverride,
  perPageOverride = RESULT_PAGE_SIZE,
}) => {
  const paramsWithoutDates = { ...queryParams };
  delete paramsWithoutDates.dates;
  delete paramsWithoutDates.startDate;
  delete paramsWithoutDates.endDate;
  const { page = 1, address, origin, ...rest } = paramsWithoutDates;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    ...rest,
    ...originMaybe,
    ...listingTypeVariantMaybe,
    page: Number(pageOverride || page) || 1,
    perPage: perPageOverride,
    include: ['author', 'images', 'author.profileImage'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'metadata',
      'deleted',
      'state',
      'publicData',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      // These help rendering of 'purchase' listings,
      // when transitioning from search page to listing page
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
      'publicData.condition',
      'publicData.brand',
      'publicData.size',
      'publicData.photos',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.firstName',
      'profile.lastName',
      'profile.publicData',
    ],
    'fields.image': [
      'variants.custom',
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      'variants.square-small',
      'variants.square-small2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig('custom', 640, 680 / 640),
    'limit.images': 5,
  };
};

const parseFilterValuesFromParam = value => {
  if (value == null) {
    return [];
  }

  const rawValue = `${value}`.trim();
  if (!rawValue) {
    return [];
  }

  const startsWithHasAll = rawValue.indexOf('has_all:') === 0;
  const startsWithHasAny = rawValue.indexOf('has_any:') === 0;
  const normalizedValue = startsWithHasAll || startsWithHasAny ? rawValue.substring(8) : rawValue;

  return normalizedValue
    .split(',')
    .map(v => `${v}`.trim())
    .filter(Boolean);
};

const extractSearchModeFromParamValue = value => {
  if (typeof value !== 'string') {
    return null;
  }
  if (value.indexOf('has_all:') === 0) {
    return 'has_all';
  }
  if (value.indexOf('has_any:') === 0) {
    return 'has_any';
  }
  return null;
};

const normalizeListingFieldFilterParamsForAPI = (params = {}, listingFields = []) => {
  const indexedListingFieldsByQueryParam = listingFields.reduce((picked, field) => {
    const isIndexedField = field?.filterConfig?.indexForSearch === true;
    if (!isIndexedField) {
      return picked;
    }

    const queryParamName = constructQueryParamName(field?.key, field?.scope);
    return queryParamName ? { ...picked, [queryParamName]: field } : picked;
  }, {});

  return Object.entries(params).reduce((picked, [paramName, paramValue]) => {
    const matchingField = indexedListingFieldsByQueryParam[paramName];
    if (!matchingField) {
      return { ...picked, [paramName]: paramValue };
    }

    const { schemaType, filterConfig = {} } = matchingField;
    if (!['enum', 'multi-enum'].includes(schemaType)) {
      return { ...picked, [paramName]: paramValue };
    }

    const selectedValues = parseFilterValuesFromParam(paramValue);
    if (selectedValues.length === 0) {
      return picked;
    }

    if (schemaType === 'multi-enum') {
      const configuredSearchMode = ['has_all', 'has_any'].includes(filterConfig?.searchMode)
        ? filterConfig.searchMode
        : 'has_all';
      const queryParamSearchMode = extractSearchModeFromParamValue(paramValue);
      const appliedSearchMode = queryParamSearchMode || configuredSearchMode;
      return {
        ...picked,
        [paramName]: `${appliedSearchMode}:${selectedValues.join(',')}`,
      };
    }

    return {
      ...picked,
      [paramName]: selectedValues.join(','),
    };
  }, {});
};

// ================ Async Thunks ================ //

/////////////////////
// Search Listings //
/////////////////////
export const querySearchPageListings = (
  { searchParams, config, append, queryKey, requestedPage, ignoredQueryParamKeys = [] },
  thunkAPI
) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const defaultFiltersConfig = getSearchPageDefaultFiltersConfig(config?.search?.defaultFilters);
  const sanitizedSearchParams = stripTutulistEncoreQueryParam(searchParams || {});
  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const constructCategoryPropertiesForAPI = (queryParamPrefix, categories, level, params) => {
    const levelKey = `${queryParamPrefix}${level}`;
    const levelValue =
      typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
    const foundCategory = categories.find(cat => cat.id === levelValue);
    const subcategories = foundCategory?.subcategories || [];
    // Note: we might need to prepare nested categories too: categoryLevel1, categoryLevel2, categoryLevel3
    return foundCategory && subcategories.length > 0
      ? {
          [levelKey]: levelValue,
          ...constructCategoryPropertiesForAPI(queryParamPrefix, subcategories, level + 1, params),
        }
      : foundCategory
      ? { [levelKey]: levelValue }
      : {};
  };

  /**
   * Category filter params are prepared here. We omit invalid category names.
   * I.e. params that are not part of the currently configured category tree.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareCategoryParams = (paramName, params) => {
    const categoryConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
    const categories = config?.categoryConfiguration?.categories || [];
    const { key, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(categoryParamPrefix)
      ? constructCategoryPropertiesForAPI(categoryParamPrefix, categories, 1, params)
      : {};
  };

  const constructIntegerRangePropertyForAPI = (queryParamPrefix, params) => {
    const integerValue = params?.[queryParamPrefix];
    const [min, max] = integerValue ? integerValue.split(',') : [];
    // NOTE: long filter needs exclusive max value on API side
    const inclusiveMin = Number.parseInt(min, 10);
    const exclusiveMax = Number.parseInt(max, 10) + 1;

    // NOTE: currently we don't validate the range values against the integer range config,
    // but we might want to do that in the future.

    return Number.isInteger(inclusiveMin) && Number.isInteger(exclusiveMax)
      ? { [queryParamPrefix]: [inclusiveMin, exclusiveMax].join(',') }
      : {};
  };

  /**
   * Integer range filter values are converted to API params of type 'long'.
   *
   * The range end must be exclusive. E.g. 1000,2000 -> 1000,2001
   *
   * NOTE: currently we don't validate the range values against the integer range config,
   * but we might want to do that in the future.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareIntegerRangeParam = (paramName, params) => {
    const integerRangeConfig = config.listing.listingFields?.find(f => f.schemaType === 'long');
    const { key, scope } = integerRangeConfig || {};
    const integerParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(integerParamPrefix)
      ? constructIntegerRangePropertyForAPI(integerParamPrefix, params)
      : {};
  };

  // This function goes through given params and if there's a specific handler for the parameter type,
  // it calls the handler to prepare the property for API.
  // Otherwise, it just passes the param through.
  const prepareAPIParams = (params, paramHandlers) => {
    const pickedKeys = Object.entries(params).reduce((picked, [k, v]) => {
      const preparedParams = paramHandlers.reduce((picked, fn) => {
        return { ...picked, ...fn(k, params) };
      }, {});

      // If the param is not handled by any of the handlers, we pass it through.
      const currentParam = Object.keys(preparedParams).length > 0 ? preparedParams : { [k]: v };

      return { ...picked, ...currentParam };
    }, {});

    return pickedKeys;
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = defaultFiltersConfig.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = defaultFiltersConfig.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    ...restOfParams
  } = sanitizedSearchParams;
  const requestedPageNumber = Number(requestedPage || sanitizedSearchParams?.page || 1);
  // The params related to default filters are prepared one-by-one
  // We could consider moving them to the prepareAPIParams function too.
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  const resolvedSort =
    sort === config.search.sortConfig.relevanceKey
      ? null
      : resolveListingSort(sort, { hasKeywords: !!restOfParams.keywords });
  const sortMaybe = resolvedSort ? { sort: resolvedSort } : {};
  const preparedParams = prepareAPIParams(restOfParams, [
    prepareCategoryParams,
    prepareIntegerRangeParam,
  ]);
  const listingFields = Array.isArray(config?.listing?.listingFields)
    ? config.listing.listingFields
    : [];
  const normalizedFilterParams = normalizeListingFieldFilterParamsForAPI(
    preparedParams,
    listingFields
  );

  const params = withPrivateListingsExcluded({
    // The params that are related to listing fields and categories are prepared here.
    // We add handler functions that check category and integer range configurations.
    // - With category params, we essentially just omit invalid category names.
    //   I.e. params that are not part of the currently configured category tree.
    // - With integer range params, we prepare the property for API.
    //   I.e. the range end must be exclusive. E.g. 1000,2000 -> 1000,2001
    // Note: invalid independent search params are still passed through
    ...normalizedFilterParams,
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    minStock: 1,
    perPage,
  });

  logSearchDebug('sdk.listings.query params', params);
  debugInfiniteScroll('fetch-request', {
    context: 'SearchPage.duck',
    page: requestedPageNumber,
    queryKey: queryKey || null,
    append: Boolean(append),
    filters: {
      keywords: params?.keywords || null,
      sort: params?.sort || null,
      price: params?.price || null,
      category: params?.pub_categoryLevel1 || null,
      listingType: params?.pub_listingType || null,
    },
  });

  const MAX_RETRIES = 2;
  const queryWithRetry = async retriesLeft => {
    try {
      return await sdk.listings.query(params);
    } catch (e) {
      if (retriesLeft > 0 && isNetworkError(e)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  return queryWithRetry(MAX_RETRIES)
    .then(response => {
      const rawListings = response?.data?.data || [];
      const filteredListings = filterPublicListings(rawListings);
      const pagination = response?.data?.meta || {};

      if (response?.data) {
        response.data.data = filteredListings;
      }
      debugInfiniteScroll('fetch-success', {
        context: 'SearchPage.duck',
        page: requestedPageNumber,
        queryKey: queryKey || null,
        append: Boolean(append),
        apiResponseCount: rawListings.length,
        filteredCount: filteredListings.length,
        totalPages: Number(pagination?.totalPages || 0),
        totalItems: Number(pagination?.totalItems || 0),
      });

      if (rawListings.length > 0 && filteredListings.length === 0) {
        log.warn(
          new Error('Search query returned results but none were renderable after filtering'),
          'search-page-results-filtered-out',
          {
            ...summarizeSearchLogContext({
              append,
              queryKey,
              requestedPage: requestedPageNumber,
              searchParams: sanitizedSearchParams,
            }),
            rawResultCount: rawListings.length,
            resultCount: filteredListings.length,
            totalItems: Number(pagination?.totalItems || 0),
          }
        );
      }

      if (shouldLogPrivateListingsDebug) {
        const filteredListingIds = new Set(
          filteredListings.map(listing => listing?.id?.uuid || listing?.id).filter(Boolean)
        );
        const privateFilteredIds = rawListings
          .filter(listing => isPrivateListing(listing))
          .map(listing => listing?.id?.uuid || listing?.id)
          .filter(Boolean);

        // eslint-disable-next-line no-console
        console.log('[private-listings] SearchPage listings filtered', {
          beforeCount: rawListings.length,
          afterCount: filteredListings.length,
          filteredPrivateCount: privateFilteredIds.length,
          sampleListingIds: privateFilteredIds
            .filter(id => !filteredListingIds.has(id))
            .slice(0, 5),
        });
      }

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      debugInfiniteScroll('fetch-failed', {
        context: 'SearchPage.duck',
        page: requestedPageNumber,
        queryKey: queryKey || null,
        append: Boolean(append),
        status: e?.status || null,
        code: e?.data?.errors?.[0]?.code || null,
        message: e?.message || null,
      });

      const isNetError = isNetworkError(e);
      const logFn = isNetError ? log.warn : log.error;
      logFn(e, append ? 'search-page-load-more-failed' : 'search-page-query-failed', {
        ...summarizeSearchLogContext({
          append,
          queryKey,
          requestedPage: requestedPageNumber,
          searchParams: sanitizedSearchParams,
        }),
        apiCode: e?.data?.errors?.[0]?.code || null,
        ignoredQueryParamKeys,
        ignoredQueryParamCount: ignoredQueryParamKeys.length,
        requestDiagnostics: summarizeSearchRequestDiagnostics(e),
      });
      const error = storableError(e);
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error);
    });
};

export const searchListings = createAsyncThunk(
  'SearchPage/searchListings',
  querySearchPageListings
);

// ================ Slice ================ //

const searchPageSlice = createSlice({
  name: 'SearchPage',
  initialState: {
    pagination: null,
    searchParams: null,
    searchInProgress: false,
    isFetchingNextPage: false,
    hasNextPage: false,
    currentPage: 0,
    loadedPages: [],
    pagesInFlight: [],
    lastFetchedQueryKey: null,
    searchListingsError: null,
    currentPageResultIds: [],
    activeListingId: null,
  },
  reducers: {
    setActiveListing: (state, action) => {
      state.activeListingId = action.payload;
    },
  },
  extraReducers: builder => {
    // Search Listings
    builder
      .addCase(searchListings.pending, (state, action) => {
        const { append = false, queryKey } = action.meta.arg;
        const effectiveQueryKey = queryKey || createSearchQueryKey(action.meta.arg.searchParams);
        const pendingPage = Number(
          action.meta.arg.requestedPage || action.meta.arg.searchParams?.page || 1
        );

        if (append) {
          // Ignore stale append request if search params already changed
          if (effectiveQueryKey !== state.lastFetchedQueryKey) {
            debugInfiniteScroll('append-pending-ignored', {
              context: 'SearchPage.reducer',
              requestedPage: pendingPage,
              queryKey: effectiveQueryKey,
              lastFetchedQueryKey: state.lastFetchedQueryKey,
            });
            return;
          }
          state.isFetchingNextPage = true;
          if (!state.pagesInFlight.includes(pendingPage)) {
            state.pagesInFlight = [...state.pagesInFlight, pendingPage].sort((a, b) => a - b);
          }
          debugInfiniteScroll('append-pending', {
            context: 'SearchPage.reducer',
            requestedPage: pendingPage,
            queryKey: effectiveQueryKey,
            currentPage: state.currentPage,
            loadedPages: state.loadedPages,
            pagesInFlight: state.pagesInFlight,
          });
          return;
        }

        state.searchParams = action.meta.arg.searchParams;
        state.searchInProgress = true;
        state.isFetchingNextPage = false;
        state.hasNextPage = false;
        state.currentPage = 0;
        state.loadedPages = [];
        state.pagesInFlight = [];
        state.lastFetchedQueryKey = effectiveQueryKey;
        state.currentPageResultIds = [];
        state.searchListingsError = null;
      })
      .addCase(searchListings.fulfilled, (state, action) => {
        const { append = false, queryKey, requestedPage } = action.meta.arg;
        const effectiveQueryKey = queryKey || createSearchQueryKey(action.meta.arg.searchParams);
        if (effectiveQueryKey !== state.lastFetchedQueryKey) {
          debugInfiniteScroll('fetch-fulfilled-ignored', {
            context: 'SearchPage.reducer',
            append: Boolean(append),
            requestedPage: Number(requestedPage || action.payload?.data?.meta?.page || 1),
            queryKey: effectiveQueryKey,
            lastFetchedQueryKey: state.lastFetchedQueryKey,
          });
          return;
        }
        const fetchedIds = resultIds(action.payload.data);
        const pagination = action.payload.data.meta;
        const fetchedPage = Number(requestedPage || pagination?.page || 1);
        const totalPages = Number(pagination?.totalPages || 0);
        const hasNextPage = totalPages > 0 ? fetchedPage < totalPages : false;

        if (append) {
          const existingCount = state.currentPageResultIds.length;
          if (!state.loadedPages.includes(fetchedPage)) {
            state.currentPageResultIds = mergeResultIds(state.currentPageResultIds, fetchedIds);
            state.loadedPages = [...state.loadedPages, fetchedPage].sort((a, b) => a - b);
          }
          state.pagesInFlight = state.pagesInFlight.filter(
            pageNumber => pageNumber !== fetchedPage
          );
          state.pagination = pagination;
          state.currentPage = getHighestLoadedPage(state.loadedPages, fetchedPage);
          state.hasNextPage = hasNextPage;
          state.isFetchingNextPage = state.pagesInFlight.length > 0;
          state.searchInProgress = false;
          debugInfiniteScroll('append-merged', {
            context: 'SearchPage.reducer',
            fetchedPage,
            queryKey: effectiveQueryKey,
            fetchedCount: fetchedIds.length,
            mergedCount: state.currentPageResultIds.length,
            mergedDelta: state.currentPageResultIds.length - existingCount,
            loadedPages: state.loadedPages,
            pagesInFlight: state.pagesInFlight,
            totalPages,
            totalItems: Number(pagination?.totalItems || 0),
            hasMore: hasNextPage,
          });
          return;
        }

        state.currentPageResultIds = fetchedIds;
        state.pagination = pagination;
        state.searchInProgress = false;
        state.isFetchingNextPage = false;
        state.currentPage = fetchedPage;
        state.hasNextPage = hasNextPage;
        state.loadedPages = [fetchedPage];
        state.pagesInFlight = [];
        debugInfiniteScroll('initial-search-merged', {
          context: 'SearchPage.reducer',
          fetchedPage,
          queryKey: effectiveQueryKey,
          fetchedCount: fetchedIds.length,
          mergedCount: state.currentPageResultIds.length,
          totalPages,
          totalItems: Number(pagination?.totalItems || 0),
          hasMore: hasNextPage,
        });
      })
      .addCase(searchListings.rejected, (state, action) => {
        const { append = false, queryKey } = action.meta.arg;
        const effectiveQueryKey = queryKey || createSearchQueryKey(action.meta.arg.searchParams);
        if (effectiveQueryKey !== state.lastFetchedQueryKey) {
          debugInfiniteScroll('fetch-rejected-ignored', {
            context: 'SearchPage.reducer',
            append: Boolean(append),
            requestedPage: Number(
              action.meta.arg.requestedPage || action.meta.arg.searchParams?.page || 1
            ),
            queryKey: effectiveQueryKey,
            lastFetchedQueryKey: state.lastFetchedQueryKey,
          });
          return;
        }
        if (append) {
          const rejectedPage = Number(
            action.meta.arg.requestedPage || action.meta.arg.searchParams?.page || 1
          );
          state.pagesInFlight = state.pagesInFlight.filter(
            pageNumber => pageNumber !== rejectedPage
          );
          state.isFetchingNextPage = state.pagesInFlight.length > 0;
          debugInfiniteScroll('append-rejected', {
            context: 'SearchPage.reducer',
            requestedPage: rejectedPage,
            queryKey: effectiveQueryKey,
            pagesInFlight: state.pagesInFlight,
            code: action.payload?.data?.errors?.[0]?.code || null,
            message: action.payload?.message || null,
          });
          return;
        }

        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.searchInProgress = false;
        state.isFetchingNextPage = false;
        state.searchListingsError = action.payload;
      });
  },
});

// Export the action creator
export const { setActiveListing } = searchPageSlice.actions;

export default searchPageSlice.reducer;

// ================ Load data ================ //

export const loadMoreListings = ({
  search,
  config,
  params: currentPathParams = {},
  page,
  queryKey,
  consecutiveHoles = 0,
}) => (dispatch, getState) => {
  const {
    ignoredQueryParamKeys,
    normalizedQueryParams,
    queryParams,
  } = getValidatedQueryParamsFromSearch({
    search,
    config,
    currentPathParams,
  });
  const { listingType: listingTypePathParam } = currentPathParams;
  const fallbackQueryKey = buildCanonicalSearchQueryKey({
    queryParams,
    config,
    listingTypePathParam,
  });

  const {
    currentPage = 0,
    hasNextPage = false,
    isFetchingNextPage = false,
    loadedPages = [],
    pagesInFlight = [],
    pagination = null,
    lastFetchedQueryKey = null,
    searchInProgress = false,
  } = getState()?.SearchPage || {};
  const effectiveQueryKey = fallbackQueryKey;
  const highestLoadedPage = getHighestLoadedPage(loadedPages, currentPage);
  const nextExpectedPage = Math.max(1, highestLoadedPage + 1);
  const callerRequestedPage = Number(page || 0);
  const requestedPage = nextExpectedPage;
  const totalPages = Number(pagination?.totalPages || 0);
  const pageAlreadyLoaded = Array.isArray(loadedPages) && loadedPages.includes(requestedPage);
  const pageInFlight = Array.isArray(pagesInFlight) && pagesInFlight.includes(requestedPage);

  debugInfiniteScroll('load-more-dispatch', {
    context: 'SearchPage.duck',
    requestedPage,
    queryKey: fallbackQueryKey,
    callerQueryKey: queryKey || null,
    callerRequestedPage: callerRequestedPage || null,
    search,
    consecutiveHoles,
  });
  const guardReason = searchInProgress
    ? 'search-in-progress'
    : isFetchingNextPage
    ? 'fetch-in-progress'
    : !hasNextPage
    ? 'no-next-page'
    : totalPages > 0 && requestedPage > totalPages
    ? 'beyond-total-pages'
    : lastFetchedQueryKey !== effectiveQueryKey
    ? 'stale-query'
    : pageInFlight
    ? 'page-in-flight'
    : pageAlreadyLoaded
    ? 'page-already-loaded'
    : null;

  if (callerRequestedPage > 0 && callerRequestedPage !== requestedPage) {
    debugInfiniteScroll('load-more-page-adjusted', {
      context: 'SearchPage.duck',
      callerRequestedPage,
      requestedPage,
      currentPage: Number(currentPage || 0),
      highestLoadedPage,
      loadedPages,
      queryKey: effectiveQueryKey,
    });
  }

  if (guardReason) {
    debugInfiniteScroll('load-more-skipped', {
      context: 'SearchPage.duck',
      requestedPage,
      expectedNextPage: nextExpectedPage,
      currentPage: Number(currentPage || 0),
      highestLoadedPage,
      queryKey: effectiveQueryKey,
      callerQueryKey: queryKey || null,
      lastFetchedQueryKey,
      loadedPages,
      pagesInFlight,
      hasNextPage,
      isFetchingNextPage,
      searchInProgress,
      reason: guardReason,
    });
    return Promise.resolve(null);
  }

  const searchParams = createListingSearchParams({
    queryParams: { ...queryParams, page: requestedPage },
    config,
    listingTypePathParam,
    pageOverride: requestedPage,
  });

  return dispatch(
    searchListings({
      searchParams,
      config,
      append: true,
      ignoredQueryParamKeys,
      queryKey: effectiveQueryKey,
      requestedPage,
    })
  )
    .unwrap()
    .then(response => {
      const pagination = response?.data?.meta || {};
      const responseListings = Array.isArray(response?.data?.data) ? response.data.data.length : 0;
      debugInfiniteScroll('load-more-success', {
        context: 'SearchPage.duck',
        requestedPage,
        queryKey: effectiveQueryKey,
        responseCount: responseListings,
        totalPages: Number(pagination?.totalPages || 0),
        totalItems: Number(pagination?.totalItems || 0),
        consecutiveHoles,
      });

      // Hole detection: if we got results from API but they were all filtered out,
      // and there are more pages, try to load the next page automatically.
      const hasMorePages = pagination.page < pagination.totalPages;
      if (responseListings === 0 && hasMorePages && consecutiveHoles < MAX_CONSECUTIVE_HOLES) {
        return dispatch(
          loadMoreListings({
            search,
            config,
            params: currentPathParams,
            queryKey: effectiveQueryKey,
            consecutiveHoles: consecutiveHoles + 1,
          })
        );
      }

      return response;
    })
    .catch(error => {
      debugInfiniteScroll('load-more-failed', {
        context: 'SearchPage.duck',
        requestedPage,
        queryKey: effectiveQueryKey,
        status: error?.status || null,
        code: error?.data?.errors?.[0]?.code || null,
        message: error?.message || null,
      });
      throw error;
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const {
    ignoredQueryParamKeys,
    normalizedQueryParams,
    queryParams,
  } = getValidatedQueryParamsFromSearch({
    search,
    config,
    currentPathParams: params,
  });
  logSearchDebug('loadData parsed query params', normalizedQueryParams);
  logSearchDebug('loadData validated query params', queryParams);
  const requestedPage = Number(normalizedQueryParams.page || 1);
  const searchParams = createListingSearchParams({
    queryParams: { ...queryParams, page: requestedPage },
    config,
    listingTypePathParam,
    pageOverride: requestedPage,
  });
  logSearchDebug('loadData search params', searchParams);
  const queryKey = buildCanonicalSearchQueryKey({
    queryParams,
    config,
    listingTypePathParam,
  });

  const { loadedPages = [], lastFetchedQueryKey = null } = state.SearchPage || {};
  const hasAlreadyLoadedRequestedPage =
    lastFetchedQueryKey === queryKey && loadedPages.includes(requestedPage);

  if (hasAlreadyLoadedRequestedPage) {
    return Promise.resolve();
  }

  const searchListingsCall = searchListings({
    searchParams,
    config,
    append: false,
    ignoredQueryParamKeys,
    queryKey,
    requestedPage,
  });

  return dispatch(searchListingsCall)
    .unwrap()
    .then(response => {
      const responseListings = Array.isArray(response?.data?.data) ? response.data.data.length : 0;
      const pagination = response?.data?.meta || {};
      const hasMorePages = pagination.page < pagination.totalPages;

      if (responseListings === 0 && hasMorePages) {
        return dispatch(
          loadMoreListings({
            search,
            config,
            params,
            queryKey,
            consecutiveHoles: 1,
          })
        );
      }
      return response;
    });
};
